import type {
  ApiReturnCampaign,
  ApiReturnProject,
  ApiReturnSite,
} from '@highimpact-frontend/api-types';

import { useCampaignsQuery } from '../query/useCampaignsQuery';

export type GridHydrationExternalsStatus = 'loading' | 'ready' | 'error';

export interface GridHydrationSnapshot {
  readonly campaigns: ApiReturnCampaign[];
  readonly project: ApiReturnProject | undefined;
  readonly site: ApiReturnSite | undefined;
}

export type GridHydrationExternalsResult =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'ready'; readonly snapshot: GridHydrationSnapshot };

export const emptyGridHydrationSnapshot = (): GridHydrationSnapshot => ({
  campaigns: [],
  project: undefined,
  site: undefined,
});

export interface UseGridHydrationExternalsOptions {
  readonly siteId: number | undefined;
  readonly projectRef: ApiReturnProject | undefined;
  readonly siteRef: ApiReturnSite | undefined;
}

/**
 * Resolves grid hydration snapshot data. Campaigns load via TanStack Query (Phase 3).
 */
export function useGridHydrationExternals({
  siteId,
  projectRef,
  siteRef,
}: UseGridHydrationExternalsOptions): GridHydrationExternalsResult {
  const hasSiteContext = siteRef !== undefined;
  const resolvedSiteId = hasSiteContext ? (siteId ?? siteRef!.id) : null;
  const { campaigns, loading, error } = useCampaignsQuery(resolvedSiteId);

  if (hasSiteContext && loading) {
    return { status: 'loading' };
  }

  if (hasSiteContext && error) {
    return {
      status: 'error',
      message: 'Impossible de charger les campagnes du site',
    };
  }

  return {
    status: 'ready',
    snapshot: {
      campaigns: hasSiteContext ? [...campaigns] : [],
      project: projectRef,
      site: siteRef,
    },
  };
}

/** @deprecated Campaigns prefetch uses TanStack Query via {@link useCampaignsQuery}. */
export const prefetchGridCampaigns = (): void => {
  // no-op
};

/** @deprecated Use {@link prefetchGridCampaigns}. */
export const prefetchGridHydrationExternals = prefetchGridCampaigns;
